import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  GraduationCap,
  Info,
} from "lucide-react";

const COLORS = {
  cdd: "#1B2A4E",
  cddLight: "#EEF1F8",
  pv: "#1B2A4E",
  pvLight: "#E5E9FD",
  orange: "#F97316",
  zinc900: "#09090B",
  zinc500: "#71717A",
  zinc200: "#E4E4E7",
};

// Coefficient indicatif net / brut pour un salarié non-cadre en France.
// Charges salariales ~ 22 % du brut → net ≈ 78 % du brut.
const NET_FROM_BRUT_RATIO = 0.78;

// --------------------------------------------------------------------
// Grille Jour-Étude officielle Passerelle V — en vigueur du 1er octobre
// 2026 au 31 décembre. Source: grille tarifaire rentrée 2026.
// --------------------------------------------------------------------
const GRILLE = [
  { ht: 118, ttc: 141.6, net: 83.39, note: "minimum L1·L2·L3 / M1·M2" },
  { ht: 120, ttc: 144.0, net: 85.19, note: null },
  { ht: 125, ttc: 150.0, net: 89.69, note: "minimum MSP HMONP" },
  { ht: 130, ttc: 156.0, net: 94.19, note: null },
  { ht: 140, ttc: 168.0, net: 103.19, note: null },
  { ht: 150, ttc: 180.0, net: 112.19, note: null },
  { ht: 160, ttc: 192.0, net: 121.19, note: null },
  { ht: 170, ttc: 204.0, net: 130.19, note: null },
  { ht: 180, ttc: 216.0, net: 139.19, note: null },
  { ht: 190, ttc: 228.0, net: 148.19, note: null },
  { ht: 200, ttc: 240.0, net: 157.19, note: null },
  { ht: 210, ttc: 252.0, net: 166.19, note: null },
];

const TVA_RATE = 0.2; // 20 % fixe (cf. site Passerelle V)

const euros = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const eurosDecimal = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n || 0);

function toNumber(v, fallback = 0) {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : fallback;
}

// ------------------------------------------------------------------
// UI primitives
// ------------------------------------------------------------------
const SectionLabel = ({ children, testId }) => (
  <div className="label-uppercase" data-testid={testId}>
    {children}
  </div>
);

const FieldLabel = ({ children, htmlFor }) => (
  <Label
    htmlFor={htmlFor}
    className="text-xs font-medium tracking-wide text-zinc-600"
  >
    {children}
  </Label>
);

const NumberField = ({
  id,
  label,
  suffix,
  value,
  onChange,
  testId,
  min,
  step = "1",
}) => (
  <div className="space-y-2">
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    <div className="relative">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="swiss-input h-12 font-mono-plex text-base tracking-tight pr-12 focus-visible:ring-0 focus-visible:border-zinc-900"
      />
      {suffix ? (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-zinc-500 font-mono-plex">
          {suffix}
        </span>
      ) : null}
    </div>
  </div>
);

const StatRow = ({ label, value, mono = true, testId }) => (
  <div className="flex items-baseline justify-between py-2.5">
    <span className="text-[0.78rem] text-zinc-500 tracking-wide">{label}</span>
    <span
      data-testid={testId}
      className={`${mono ? "font-mono-plex" : ""} text-sm text-zinc-900`}
    >
      {value}
    </span>
  </div>
);

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
export default function Calculette() {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get("embed") === "1";

  // Inputs
  const [salaireBrut, setSalaireBrut] = useState("1900");
  const [chargesPct, setChargesPct] = useState("42");

  // Sélection dans la grille (par défaut : palier 118 € HT - minimum L1·L2·L3 / M1·M2)
  const [tjmHt, setTjmHt] = useState(118);
  const [joursMois, setJoursMois] = useState("21");
  const [tvaRecup, setTvaRecup] = useState(true);

  const [duree, setDuree] = useState(6);

  const palier = useMemo(
    () => GRILLE.find((g) => g.ht === Number(tjmHt)) || GRILLE[0],
    [tjmHt],
  );

  // Auto-resize iframe (mode embed) -------------------------------
  useEffect(() => {
    if (!isEmbed) return undefined;
    const send = () => {
      try {
        const h = document.documentElement.scrollHeight;
        window.parent.postMessage(
          { type: "calculette-v:height", height: h },
          "*",
        );
      } catch (e) {
        /* noop */
      }
    };
    send();
    const ro = new ResizeObserver(send);
    ro.observe(document.documentElement);
    window.addEventListener("load", send);
    const interval = setInterval(send, 1000); // safety net
    return () => {
      ro.disconnect();
      window.removeEventListener("load", send);
      clearInterval(interval);
    };
  }, [isEmbed]);

  // Calculs --------------------------------------------------------
  const calc = useMemo(() => {
    const sal = toNumber(salaireBrut);
    const ch = toNumber(chargesPct);
    const jm = toNumber(joursMois);
    const d = Math.max(1, Math.min(12, Math.round(duree)));

    // CDD
    const cddMensuel = sal * (1 + ch / 100);
    const cddTotal = cddMensuel * d;
    const cddNetMensuel = sal * NET_FROM_BRUT_RATIO; // revenu net salarié / mois

    // Passerelle V — basé sur le palier de la grille
    const pvMensuelHT = palier.ht * jm;
    const pvTotalHT = pvMensuelHT * d;
    const pvTVA = pvTotalHT * TVA_RATE;
    const pvTotal = tvaRecup ? pvTotalHT : pvTotalHT + pvTVA;
    const pvMensuelEffectif = tvaRecup
      ? pvMensuelHT
      : pvMensuelHT * (1 + TVA_RATE);
    const pvNetMensuelEtudiant = palier.net * jm; // revenu net étudiant / mois

    const diff = cddTotal - pvTotal;
    const diffPct =
      Math.max(cddTotal, pvTotal) > 0
        ? (Math.abs(diff) / Math.max(cddTotal, pvTotal)) * 100
        : 0;
    const pvCheaper = diff > 0;
    const cddCheaper = diff < 0;

    // Alerte conservée : nb total de jours > 130
    const alerts = [];
    const joursTotaux = jm * d;
    if (joursTotaux > 130) {
      alerts.push(
        `Durée maximale recommandée dépassée (${Math.round(joursTotaux)} / 130 jours)`,
      );
    }

    return {
      d,
      cddMensuel,
      cddTotal,
      cddNetMensuel,
      pvMensuelHT,
      pvTotalHT,
      pvTVA,
      pvTotal,
      pvMensuelEffectif,
      pvNetMensuelEtudiant,
      diff,
      diffPct,
      pvCheaper,
      cddCheaper,
      alerts,
    };
  }, [salaireBrut, chargesPct, palier, joursMois, tvaRecup, duree]);

  return (
    <div
      className="min-h-screen bg-[#F598FF] text-zinc-900"
      data-testid="calculette-page"
      data-embed={isEmbed ? "1" : "0"}
    >
      {/* Header retiré : la calculette est conçue pour vivre embarquée sur passerelle-v.fr/fonctionnement où le titre de section sert déjà de header. */}

      <main
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
          isEmbed ? "py-6 sm:py-8 space-y-10" : "py-10 sm:py-12 space-y-12"
        }`}
      >
        {/* ----------------------------------------------------------- */}
        {/* INPUTS                                                      */}
        {/* ----------------------------------------------------------- */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <SectionLabel testId="section-inputs-label">
              01 — Paramètres
            </SectionLabel>
            <span className="text-xs text-zinc-400 font-mono-plex">
              Mise à jour instantanée
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
            {/* --------------------------- CDD --------------------------- */}
            <div
              className="col-span-1 lg:col-span-5 bg-white border border-zinc-200 p-6 sm:p-8 flex flex-col"
              data-testid="cdd-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-9 h-9 border flex items-center justify-center"
                  style={{
                    borderColor: COLORS.cdd,
                    backgroundColor: COLORS.cddLight,
                  }}
                >
                  <Building2 size={16} style={{ color: COLORS.cdd }} />
                </div>
                <div>
                  <div className="label-uppercase" style={{ color: COLORS.cdd }}>
                    CDD
                  </div>
                  <div className="font-condensed text-2xl tracking-tight text-zinc-900">
                    Salarié interne
                  </div>
                </div>
              </div>

              <div className="space-y-5 flex-1">
                <NumberField
                  id="cdd-salaire"
                  label="Salaire brut mensuel"
                  suffix="€"
                  value={salaireBrut}
                  onChange={setSalaireBrut}
                  testId="cdd-salary-input"
                  min={0}
                />
                <NumberField
                  id="cdd-charges"
                  label="Taux de charges patronales"
                  suffix="%"
                  value={chargesPct}
                  onChange={setChargesPct}
                  testId="cdd-charges-input"
                  min={0}
                  step="0.1"
                />
              </div>

              <Separator className="my-6 bg-zinc-200" />
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-zinc-500">Coût employeur</span>
                <span
                  className="font-mono-plex text-2xl font-light tracking-tight"
                  style={{ color: COLORS.cdd }}
                  data-testid="cdd-monthly-cost-preview"
                >
                  {euros(calc.cddMensuel)}
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-xs text-zinc-500">
                  Net salarié (non cadre)
                </span>
                <span
                  className="font-mono-plex text-base text-zinc-900"
                  data-testid="cdd-monthly-net-preview"
                >
                  {euros(calc.cddNetMensuel)}
                </span>
              </div>
            </div>

            {/* --------------------------- VS --------------------------- */}
            <div className="col-span-1 lg:col-span-2 flex lg:flex-col items-center justify-center gap-3 py-4 lg:py-0">
              <div className="w-10 h-10 border border-zinc-900 flex items-center justify-center bg-white">
                <ArrowRight size={16} className="text-zinc-900 lg:rotate-90" />
              </div>
              <span className="label-uppercase text-center">vs</span>
            </div>

            {/* --------------------- Passerelle V --------------------- */}
            <div
              className="col-span-1 lg:col-span-5 bg-white border border-zinc-200 p-6 sm:p-8 flex flex-col"
              data-testid="pv-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-9 h-9 border flex items-center justify-center"
                  style={{
                    borderColor: COLORS.pv,
                    backgroundColor: COLORS.pvLight,
                  }}
                >
                  <GraduationCap size={16} style={{ color: COLORS.pv }} />
                </div>
                <div>
                  <div className="label-uppercase" style={{ color: COLORS.pv }}>
                    Passerelle V
                  </div>
                  <div className="font-condensed text-2xl tracking-tight text-zinc-900">
                    Mission Étudiant
                  </div>
                </div>
              </div>

              <div className="space-y-5 flex-1">
                {/* Grille tarifaire */}
                <div className="space-y-2">
                  <FieldLabel htmlFor="pv-tjm">Prix Jour-Étude HT</FieldLabel>
                  <Select
                    value={String(tjmHt)}
                    onValueChange={(v) => setTjmHt(Number(v))}
                  >
                    <SelectTrigger
                      id="pv-tjm"
                      className="swiss-input h-12 font-mono-plex text-base rounded-none focus:ring-0 focus:border-zinc-900 data-[state=open]:border-zinc-900"
                      data-testid="pv-daily-rate-select"
                    >
                      <SelectValue placeholder="Choisir un palier" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {GRILLE.map((g) => (
                        <SelectItem
                          key={g.ht}
                          value={String(g.ht)}
                          className="font-mono-plex rounded-none"
                          data-testid={`pv-tjm-option-${g.ht}`}
                        >
                          {eurosDecimal(g.ht)} HT
                          {g.note ? (
                            <span className="ml-2 text-xs text-zinc-500">
                              · {g.note}
                            </span>
                          ) : null}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[0.7rem] text-zinc-500 leading-snug">
                    Grille en vigueur du 1ᵉʳ octobre au 31 décembre 2026 —
                    fixée par Passerelle V.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 items-end">
                  <NumberField
                    id="pv-jours"
                    label="Jours / mois"
                    suffix="j"
                    value={joursMois}
                    onChange={setJoursMois}
                    testId="pv-days-input"
                    min={0}
                  />
                  <div className="flex items-center justify-between h-12 border border-zinc-200 px-4">
                    <span className="text-xs text-zinc-700 font-medium leading-tight">
                      TVA 20 %<br />
                      <span className="text-[0.65rem] text-zinc-500">
                        récupérable
                      </span>
                    </span>
                    <Switch
                      checked={tvaRecup}
                      onCheckedChange={setTvaRecup}
                      data-testid="pv-vat-recoverable-switch"
                      className="data-[state=checked]:bg-zinc-900"
                    />
                  </div>
                </div>

                {/* Détail palier choisi */}
                <div className="border border-zinc-200 bg-zinc-50/60 p-4">
                  <div className="label-uppercase mb-3">Détail du palier</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[0.65rem] text-zinc-500 uppercase tracking-wider">
                        HT Agence
                      </div>
                      <div
                        className="font-mono-plex text-base mt-1"
                        data-testid="pv-detail-ht"
                      >
                        {eurosDecimal(palier.ht)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[0.65rem] text-zinc-500 uppercase tracking-wider">
                        TTC Agence
                      </div>
                      <div
                        className="font-mono-plex text-base mt-1"
                        data-testid="pv-detail-ttc"
                      >
                        {eurosDecimal(palier.ttc)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[0.65rem] text-zinc-500 uppercase tracking-wider">
                        Net étudiant
                      </div>
                      <div
                        className="font-mono-plex text-base mt-1"
                        style={{ color: COLORS.pv }}
                        data-testid="pv-detail-net"
                      >
                        {eurosDecimal(palier.net)}
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-[0.68rem] text-zinc-500 leading-snug">
                    Net étudiant = HT − frais Passerelle V (10 %) − cotisations
                    (base URSSAF forfaitaire). TVA déductible fiscalement.
                  </p>
                </div>
              </div>

              <Separator className="my-6 bg-zinc-200" />
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-zinc-500">
                  Coût employeur {tvaRecup ? "HT" : "TTC"}
                </span>
                <span
                  className="font-mono-plex text-2xl font-light tracking-tight"
                  style={{ color: COLORS.pv }}
                  data-testid="pv-monthly-cost-preview"
                >
                  {euros(calc.pvMensuelEffectif)}
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-xs text-zinc-500">Net étudiant</span>
                <span
                  className="font-mono-plex text-base text-zinc-900"
                  data-testid="pv-monthly-net-preview"
                >
                  {euros(calc.pvNetMensuelEtudiant)}
                </span>
              </div>
            </div>
          </div>

          {/* ---------------- Slider Durée ---------------- */}
          <div className="mt-8 bg-white border border-zinc-200 p-6 sm:p-8">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <SectionLabel>Durée de la mission</SectionLabel>
                <div className="mt-2 font-display text-3xl font-light tracking-tight">
                  <span data-testid="duration-value">{calc.d}</span>
                  <span className="text-zinc-400 text-xl ml-2">mois</span>
                </div>
              </div>
              <div className="text-xs text-zinc-500 font-mono-plex">1 → 12</div>
            </div>

            <Slider
              min={1}
              max={12}
              step={1}
              value={[calc.d]}
              onValueChange={(v) => setDuree(v[0])}
              data-testid="duration-slider"
              className="swiss-slider"
            />

            <div className="mt-3 flex justify-between text-[0.65rem] text-zinc-400 font-mono-plex">
              {Array.from({ length: 12 }, (_, i) => (
                <span
                  key={i}
                  className={i + 1 === calc.d ? "text-zinc-900" : ""}
                >
                  {i + 1}
                </span>
              ))}
            </div>
          </div>

          {/* ---------------- Alertes ---------------- */}
          {calc.alerts.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="alerts-container">
              {calc.alerts.map((a, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 border-l-2 border-[#F97316] bg-white px-4 py-3"
                  data-testid={`alert-${idx}`}
                >
                  <AlertTriangle
                    size={16}
                    className="mt-0.5 shrink-0"
                    style={{ color: COLORS.orange }}
                  />
                  <span className="text-sm text-zinc-700">{a}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ----------------------------------------------------------- */}
        {/* RESULTS + COMPARISON                                        */}
        {/* ----------------------------------------------------------- */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <SectionLabel>02 — Résultats sur {calc.d} mois</SectionLabel>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* ---------- CDD result ---------- */}
            <div
              className="border p-6 sm:p-8 flex flex-col transition-all duration-300"
              style={
                calc.cddCheaper
                  ? {
                      backgroundColor: COLORS.cddLight,
                      borderColor: COLORS.cdd,
                    }
                  : {
                      backgroundColor: "#fff",
                      borderColor: COLORS.zinc200,
                      opacity: 0.7,
                    }
              }
              data-testid="cdd-result-card"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="label-uppercase" style={{ color: COLORS.cdd }}>
                  CDD
                </div>
              </div>
              <div className="font-display text-4xl sm:text-5xl font-light tracking-tighter text-zinc-900">
                <span data-testid="cdd-total-cost-result">
                  {euros(calc.cddTotal)}
                </span>
              </div>
              <Separator className="my-5 bg-zinc-200" />
              <StatRow
                label="Coût mensuel employeur"
                value={euros(calc.cddMensuel)}
                testId="cdd-monthly-cost-result"
              />
              <StatRow
                label="Net salarié / mois"
                value={euros(calc.cddNetMensuel)}
                testId="cdd-net-salary-result"
              />
              <p className="mt-2 text-[0.65rem] text-zinc-400 leading-snug">
                Estimation indicative — base 78 % du brut (charges salariales
                ≈ 22 %).
              </p>
            </div>

            {/* ---------- Passerelle V result ---------- */}
            <div
              className="border p-6 sm:p-8 flex flex-col transition-all duration-300"
              style={
                calc.pvCheaper
                  ? {
                      backgroundColor: COLORS.pvLight,
                      borderColor: COLORS.pv,
                    }
                  : {
                      backgroundColor: "#fff",
                      borderColor: COLORS.zinc200,
                      opacity: 0.7,
                    }
              }
              data-testid="pv-result-card"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="label-uppercase" style={{ color: COLORS.pv }}>
                  Passerelle V
                </div>
              </div>
              <div className="font-display text-4xl sm:text-5xl font-light tracking-tighter text-zinc-900">
                <span data-testid="pv-total-cost-result">
                  {euros(calc.pvTotal)}
                </span>
                <span className="text-base text-zinc-500 ml-2">
                  {tvaRecup ? "HT" : "TTC"}
                </span>
              </div>
              <Separator className="my-5 bg-zinc-200" />
              <StatRow
                label="Coût mensuel HT"
                value={euros(calc.pvMensuelHT)}
                testId="pv-monthly-ht-result"
              />
              <StatRow
                label="Total HT"
                value={euros(calc.pvTotalHT)}
                testId="pv-total-ht-result"
              />
              <StatRow
                label="TVA (20 %)"
                value={euros(calc.pvTVA)}
                testId="pv-vat-amount-result"
              />
              <StatRow
                label="Total TTC"
                value={euros(calc.pvTotalHT + calc.pvTVA)}
                testId="pv-total-ttc-result"
              />
              <StatRow
                label="Net étudiant / mois"
                value={euros(calc.pvNetMensuelEtudiant)}
                testId="pv-net-student-result"
              />
              <p className="mt-2 text-[0.65rem] text-zinc-400 leading-snug">
                Net étudiant = palier (
                {eurosDecimal(palier.net)}) × jours travaillés / mois.
              </p>
            </div>

            {/* ---------- Comparison ---------- */}
            <div
              className="border bg-zinc-900 text-white p-6 sm:p-8 flex flex-col"
              data-testid="comparison-card"
            >
              <div className="label-uppercase" style={{ color: "#A1A1AA" }}>
                Comparaison
              </div>

              <div className="font-display text-4xl sm:text-5xl font-light tracking-tighter mt-5">
                <span data-testid="comparison-difference">
                  {euros(Math.abs(calc.diff))}
                </span>
              </div>
              <div className="mt-1 font-mono-plex text-sm text-zinc-400">
                soit&nbsp;
                <span data-testid="comparison-percentage">
                  {calc.diffPct.toFixed(1).replace(".", ",")}%
                </span>{" "}
                d&apos;écart
              </div>

              <Separator className="my-6 bg-zinc-700" />

              <p
                className="text-sm leading-relaxed text-zinc-200"
                data-testid="comparison-verdict"
              >
                {calc.pvCheaper && (
                  <>
                    Vous économisez{" "}
                    <span
                      className="font-semibold underline decoration-1 underline-offset-4"
                      style={{ color: "#FFFFFF" }}
                    >
                      {euros(Math.abs(calc.diff))}
                    </span>{" "}
                    avec Passerelle V.
                  </>
                )}
                {calc.cddCheaper && (
                  <>
                    Le CDD est plus rentable de{" "}
                    <span
                      className="font-semibold underline decoration-1 underline-offset-4"
                      style={{ color: "#FFFFFF" }}
                    >
                      {euros(Math.abs(calc.diff))}
                    </span>
                    .
                  </>
                )}
                {!calc.pvCheaper && !calc.cddCheaper && (
                  <>Les deux options sont équivalentes sur cette durée.</>
                )}
              </p>
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------------- */}
        {/* Disclaimer                                                  */}
        {/* ----------------------------------------------------------- */}
        <section
          className="border-t border-zinc-200 pt-10"
          data-testid="disclaimer-section"
        >
          <div className="flex items-start gap-3 max-w-3xl">
            <Info size={16} className="mt-1 shrink-0 text-zinc-400" />
            <p className="text-xs text-zinc-500 leading-relaxed">
              Simulation indicative. Passerelle V intervient dans le cadre d&apos;un
              système contractuel tripartite (Passerelle V / étudiant / structure
              d&apos;accueil) au moyen de conventions d&apos;étude. Ce ne sont ni
              des contrats de travail ni des conventions de stage, sans lien de
              subordination équivalent à un contrat de travail.
            </p>
          </div>
        </section>
      </main>

      {/* Footer retiré : non nécessaire en embed et redondant en standalone. */}
    </div>
  );
}
